import { pxToEm } from 'util/pxToEm';
import React, { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { ExportDownloadIcon } from 'components/icons';

export interface ExportButtonProps {
  onClick: () => void;
  disabled?: boolean;
  /** Shows "Exporting..." when true */
  loading?: boolean;
  /** Visible label (default "Export Data"; use "Export" for toolbars). */
  label?: string;
  /**
   * When true, hides the label on extra-small viewports (matches org detail analytics toolbar).
   */
  hideLabelOnMobile?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Bordered "Export" control used across admin toolbars (analytics, attendees,
 * leaders, and the users page).
 */
export const ExportButton = ({
  onClick,
  disabled = false,
  loading = false,
  label = 'Export Data',
  hideLabelOnMobile = false,
  sx: sxProp,
}: ExportButtonProps): ReactElement => {
  const busy = disabled || loading;
  return (
    <Box
      component="button"
      type="button"
      display="flex"
      alignItems="center"
      gap={8}
      onClick={busy ? undefined : onClick}
      disabled={busy}
      sx={[
        {
          height: 50,
          cursor: busy ? 'not-allowed' : 'pointer',
          opacity: busy ? 0.6 : 1,
          border: '1px solid #DEDEDE',
          borderRadius: '8px',
          padding: '8px 16px',
          backgroundColor: '#FFFFFF',
          fontFamily: 'inherit',
          '&:hover': {
            backgroundColor: busy ? '#FFFFFF' : '#F5F5F5',
          },
        },
        ...(sxProp ? (Array.isArray(sxProp) ? sxProp : [sxProp]) : []),
      ]}
    >
      <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ExportDownloadIcon style={{ width: 14, height: 14 }} />
      </Box>
      <Typography
        color="text.primary"
        fontSize={pxToEm(14)}
        fontWeight={400}
        lineHeight={pxToEm(21)}
        sx={{
          display: hideLabelOnMobile
            ? { xs: 'none', md: 'block' }
            : 'block',
        }}
      >
        {loading ? 'Exporting...' : label}
      </Typography>
    </Box>
  );
};
